import { useEffect, useRef, useState } from 'react';

/* ─── Helpers ─────────────────────────────────────────────────────────────── */

function extractYoutubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:v=|youtu\.be\/|youtube\.com\/(?:embed|shorts|live)\/)([^&?/]+)/);
  return m ? m[1] : null;
}

function extractTikTokId(url) {
  if (!url) return null;
  const m = url.match(/(?:\/video\/|\/embed\/v2\/)(\d+)/);
  return m ? m[1] : null;
}

function extractInstagramId(url) {
  if (!url) return null;
  const m = url.match(/instagram\.com\/(?:p|reel|tv)\/([\w-]+)/);
  return m ? m[1] : null;
}

function detectPlatform(url) {
  if (!url) return null;
  const u = url.toLowerCase();
  if (u.includes('youtube.com') || u.includes('youtu.be')) return 'youtube';
  if (u.includes('tiktok.com')) return 'tiktok';
  if (u.includes('instagram.com')) return 'instagram';
  if (u.includes('facebook.com') || u.includes('fb.watch')) return 'facebook';
  return null;
}

// Decode &amp; that the backend sometimes stores in embed URLs
function decodeEmbedUrl(url) {
  return url ? url.replace(/&amp;/g, '&') : null;
}

/* ─── Shared wrapper styles ───────────────────────────────────────────────── */

const iframeStyle = { width: '100%', height: '100%', border: 'none' };

/* ─── YouTube ─────────────────────────────────────────────────────────────── */

export function YouTubePlayer({ video, fallbackUrl }) {
  const embedUrl = decodeEmbedUrl(video?.primary_embed_url);
  const videoId =
    extractYoutubeId(embedUrl) ||
    extractYoutubeId(video?.lien_primaire) ||
    extractYoutubeId(fallbackUrl);

  if (!videoId) return <VideoLinkFallback url={video?.lien_primaire || fallbackUrl} platform="YouTube" />;

  return (
    <div className="direct-embed-frame">
      <iframe
        className="video-embed"
        src={`https://www.youtube.com/embed/${videoId}`}
        title={video?.theme || video?.date || 'YouTube video'}
        allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
        allowFullScreen
        loading="lazy"
      />
    </div>
  );
}

/* ─── Facebook ────────────────────────────────────────────────────────────── */

export function FacebookPlayer({ video, fallbackUrl }) {
  const link = video?.lien_primaire || fallbackUrl || '';
  const isReel = /\/reel\/|\/reels\//i.test(link);
  const containerRef = useRef(null);
  const [pxWidth, setPxWidth] = useState(0);

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(entries => {
      const w = Math.floor(entries[0].contentRect.width);
      if (w > 0) setPxWidth(w);
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const embedUrl = decodeEmbedUrl(video?.primary_embed_url);
  if (!embedUrl) return <VideoLinkFallback url={link} platform="Facebook" />;

  // Inject measured pixel width into the embed URL
  const w = pxWidth || 500;
  const sizedUrl = embedUrl.replace(/width=\d+/, `width=${w}`);

  return isReel ? (
    <div className={`direct-embed-frame${isReel ? ' facebook-reel-frame' : ''}`} ref={containerRef}>
      <iframe
        className="video-embed"
        src={sizedUrl}
        title={video?.theme || video?.date || 'Facebook Reel'}
        width={w}
        height="100%"
        style={{ width: '100%', height: '100%', border: 'none' }}
        allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
        allowFullScreen
        loading="lazy"
      />
    </div>
  ) : (
    <div className="direct-embed-frame" ref={containerRef}>
      <iframe
        className="video-embed"
        src={sizedUrl}
        title={video?.theme || video?.date || 'Facebook video'}
        width={w}
        height="100%" 
        style={{ width: '100%', height: '100%', border: 'none' }}
        allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
        allowFullScreen
        loading="lazy"
      />
    </div>
  );
}

/* ─── TikTok ──────────────────────────────────────────────────────────────── */

export function TikTokPlayer({ video, fallbackUrl }) {
  const embedUrl = decodeEmbedUrl(video?.primary_embed_url);
  const tiktokId = 
    (video?.primary_video_id && /^\d+$/.test(video.primary_video_id) ? video.primary_video_id : null) ||
    extractTikTokId(embedUrl) ||
    extractTikTokId(video?.lien_primaire) ||
    extractTikTokId(fallbackUrl);

  const src = tiktokId
    ? `https://www.tiktok.com/embed/v2/${tiktokId}`
    : embedUrl || null;

  if (!src) return <VideoLinkFallback url={video?.lien_primaire || fallbackUrl} platform="TikTok" />;

  return (
    <div style={{
      width: '100%', maxWidth: 325, margin: '0 auto 24px',
      aspectRatio: '9 / 16', borderRadius: 12, overflow: 'hidden', background: '#000',
    }}>
      <iframe
        style={iframeStyle}
        src={src}
        title={video?.theme || video?.date || 'TikTok video'}
        allow="autoplay; encrypted-media; picture-in-picture; web-share"
        allowFullScreen
        scrolling="no"
        loading="lazy"
      />
    </div>
  );
}

/* ─── Instagram ───────────────────────────────────────────────────────────── */

export function InstagramPlayer({ video, fallbackUrl }) {
  const shortcode =
    extractInstagramId(video?.lien_primaire) ||
    extractInstagramId(fallbackUrl);

  const embedUrl = shortcode ? `https://www.instagram.com/p/${shortcode}/embed/` : null;

  if (!embedUrl) return <VideoLinkFallback url={video?.lien_primaire || fallbackUrl} platform="Instagram" />;

  return (
    <div style={{
      width: '100%', maxWidth: 480, margin: '0 auto 24px',
      borderRadius: 12, overflow: 'hidden', background: '#fff', minHeight: 480,
    }}>
      <iframe
        style={{ ...iframeStyle, height: 560 }}
        src={embedUrl}
        title={video?.theme || video?.date || 'Instagram video'}
        allow="autoplay; encrypted-media; picture-in-picture; web-share"
        allowFullScreen
        scrolling="no"
        loading="lazy"
      />
    </div>
  );
}

/* ─── Audio Player ────────────────────────────────────────────────────────── */

export function AudioPlayer({ video }) {
  return (
    <div style={{
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 60%, #0f3460 100%)',
      borderRadius: 16, padding: '16px 16px 12px', color: '#fff',
      boxShadow: '0 8px 32px rgba(0,0,0,0.22)', marginBottom: 24,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <div style={{
          width: 40, height: 40, borderRadius: '50%',
          background: 'rgba(255,255,255,0.12)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <svg viewBox="0 0 24 24" fill="none" width="20" height="20">
            <rect x="9" y="2" width="6" height="11" rx="3" fill="white" />
            <path d="M5 10a7 7 0 0014 0" stroke="white" strokeWidth="1.8" strokeLinecap="round" fill="none" />
            <line x1="12" y1="17" x2="12" y2="21" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
            <line x1="8" y1="21" x2="16" y2="21" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </div>
        <div>
          <div style={{ fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)', marginBottom: 1 }}>
            Témoignage audio
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.2 }}>
            {video?.theme || video?.intervenant || 'Enregistrement'}
          </div>
        </div>
      </div>
      <audio
        controls
        src={video?.recording_url}
        style={{ width: '100%', height: 32, borderRadius: 6, outline: 'none', filter: 'invert(1) hue-rotate(180deg)' }}
      >
        Votre navigateur ne supporte pas la lecture audio.
      </audio>
    </div>
  );
}

/* ─── Fallback link button ────────────────────────────────────────────────── */
function VideoLinkFallback({ url, platform }) {
  if (!url) return null;
  return (
    <div style={{ marginBottom: 24, textAlign: 'center', padding: '40px 20px', background: '#f8f8f8', borderRadius: 12 }}>
      <div style={{ fontSize: 14, color: '#555', marginBottom: 16 }}>
        Cette vidéo ne peut pas être intégrée directement.
      </div>
      <a href={url} target="_blank" rel="noreferrer" className="btn-mcm" style={{ display: 'inline-block' }}>
        ▶ Regarder sur {platform || 'la plateforme'}
      </a>
    </div>
  );
}

/* ─── Loading skeleton ────────────────────────────────────────────────────── */

export function VideoLoadingSkeleton() {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{
        width: '100%', aspectRatio: '16 / 9',
        background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
        backgroundSize: '200% 100%', animation: 'vp-loading 1.5s infinite', borderRadius: 8,
      }} />
      <style>{`@keyframes vp-loading{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
      <div style={{ fontSize: 12, color: '#888', textAlign: 'center', marginTop: 8 }}>Chargement de la vidéo...</div>
    </div>
  );
}

/* ─── VideoPreview orchestrator ───────────────────────────────────────────── */

export function VideoPreview({ video, fallbackUrl, loading }) {
  if (loading) return <VideoLoadingSkeleton />;
  if (!video && !fallbackUrl) return null;

  const recUrl = video?.recording_url;
  const isAudio = recUrl && /\.(mp3|wav|ogg|aac|m4a|flac)(\b|$)/i.test(recUrl.split('?')[0]);
  const isRecordedVideo = recUrl && /\.(mp4|mov|avi|webm|mkv|m4v)(\b|$)/i.test(recUrl.split('?')[0]);

  if (isAudio) return <AudioPlayer video={video} />;

  if (isRecordedVideo) {
    return (
      <div className="direct-embed-frame">
        <video controls src={recUrl} className="video-embed" style={{ objectFit: 'contain', background: '#000' }}>
          Votre navigateur ne supporte pas la lecture vidéo.
        </video>
      </div>
    );
  }

  const platform =
    video?.primary_platform ||
    detectPlatform(video?.lien_primaire) ||
    detectPlatform(fallbackUrl);

  switch (platform) {
    case 'youtube':   return <YouTubePlayer video={video} fallbackUrl={fallbackUrl} />;
    case 'facebook':  return <FacebookPlayer video={video} fallbackUrl={fallbackUrl} />;
    case 'tiktok':    return <TikTokPlayer video={video} fallbackUrl={fallbackUrl} />;
    case 'instagram': return <InstagramPlayer video={video} fallbackUrl={fallbackUrl} />;
    default: {
      const embedUrl = decodeEmbedUrl(video?.primary_embed_url);
      if (embedUrl) {
        return (
          <div className="direct-embed-frame">
            <iframe
              className="video-embed"
              src={embedUrl}
              title={video?.theme || video?.date || 'Vidéo'}
              allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
              allowFullScreen
              loading="lazy"
            />
          </div>
        );
      }
      return <VideoLinkFallback url={video?.lien_primaire || fallbackUrl} platform="la plateforme" />;
    }
  }
}
